import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'

import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { Monogram } from '@/components/site/founder'
import { CtaBand, Eyebrow, PageHero, SectionHeader } from '@/components/site/primitives'
import { useLocale } from '@/i18n/use-locale'
import { siteAbout } from '@/i18n/site-about'

import {
    BookOpen,
    Clock,
    Cpu,
    Eye,
    FileCheck,
    FilePen,
    GraduationCap,
    KeyRound,
    Languages,
    Lock,
    MapPin,
    MessageSquare,
    ScanSearch,
    ShieldCheck,
    UserCheck,
    Users
} from 'lucide-react'

const principleIcons = [UserCheck, Eye, FilePen, Lock, MessageSquare, Cpu]
const qualityIcons = [FileCheck, ScanSearch, ShieldCheck, KeyRound, BookOpen, Users]
const reachIcons = [Languages, Clock, MapPin]

const AboutPage: FC = (): ReactNode => {
    const locale = useLocale()
    const copy = siteAbout[locale]
    const founder = copy.founder

    const person = {
        '@context': 'https://schema.org',
        '@type': 'Person',
        name: 'Qëndrim Uka',
        jobTitle: founder.role,
        knowsLanguage: ['sq', 'en'],
        alumniOf: { '@type': 'CollegeOrUniversity', name: 'University of Prishtina “Hasan Prishtina”' },
        worksFor: { '@type': 'ProfessionalService', name: 'Ura Advisory' }
    }

    return (
        <Layout tone='dark'>
            <Seo
                title={copy.seoTitle}
                description={copy.seoDescription}
                path='/about'
                jsonLd={[person]}
                breadcrumbs={[{ name: copy.breadcrumb, path: '/about' }]}
            />

            <PageHero
                tone='dark'
                eyebrow={copy.hero.eyebrow}
                title={copy.hero.title}
                text={copy.hero.text}
                aside={(
                    <div className='space-y-3'>
                        <p className='text-xs font-semibold uppercase tracking-[0.18em] text-white/55'>
                            {copy.hero.bridgesLabel}
                        </p>

                        {copy.hero.bridges.map(bridge => (
                            <div key={bridge.left} className='rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur'>
                                <div className='flex items-center gap-3 text-sm font-medium'>
                                    <span className='shrink-0 text-white'>{bridge.left}</span>

                                    <span className='relative h-px flex-1 bg-white/25' aria-hidden='true'>
                                        <span className='absolute -top-[3px] left-0 size-[7px] rounded-full bg-white' />
                                        <span className='absolute -top-[3px] right-0 size-[7px] rounded-full bg-white' />
                                        <span className='absolute -top-[3px] left-1/2 size-[7px] -translate-x-1/2 rounded-full bg-signal' />
                                    </span>

                                    <span className='shrink-0 text-right text-white'>{bridge.right}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            />

            <section id='the-name' className='bg-background py-24'>
                <div className='container-page grid gap-12 lg:grid-cols-2 lg:items-center lg:gap-20'>
                    <div className='relative'>
                        <img
                            src='/images/prishtina.jpg'
                            alt={copy.name.imageAlt}
                            width={1500}
                            height={1500}
                            loading='lazy'
                            className='aspect-[4/5] w-full rounded-3xl object-cover'
                        />

                        <div className='absolute -bottom-6 -right-4 hidden rounded-2xl bg-ink px-6 py-5 text-white shadow-2xl shadow-ink/30 sm:block'>
                            <p className='font-display text-4xl leading-none'>ura</p>
                            <p className='mt-1 text-xs uppercase tracking-[0.2em] text-white/55'>{copy.name.eyebrow}</p>
                        </div>
                    </div>

                    <div>
                        <Eyebrow>{copy.name.eyebrow}</Eyebrow>

                        <h2 className='mt-4 font-display text-[2.4rem] leading-[1.05] sm:text-5xl'>
                            {copy.name.title}
                        </h2>

                        <div className='mt-8 space-y-5 text-lg leading-relaxed text-muted-foreground'>
                            {copy.name.paragraphs.map((paragraph, index) => (
                                <p key={paragraph} className={index === 0 ? 'text-xl text-foreground' : undefined}>
                                    {paragraph}
                                </p>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section id='founder' className='scroll-mt-20 bg-mist py-24'>
                <div className='container-page grid gap-12 lg:grid-cols-[340px_1fr] lg:gap-16'>
                    <div>
                        <div className='rounded-3xl bg-ink p-8 text-white lg:sticky lg:top-28'>
                            <Monogram className='mx-auto max-w-[220px]' />

                            <p className='mt-7 text-xl font-semibold'>{founder.title}</p>
                            <p className='text-sm text-white/60'>{founder.role}</p>

                            <dl className='mt-6 space-y-4 border-t border-white/10 pt-6 text-sm'>
                                {founder.facts.map(fact => (
                                    <div key={fact.label}>
                                        <dt className='text-xs font-semibold uppercase tracking-[0.14em] text-white/55'>{fact.label}</dt>
                                        <dd className='mt-1 text-white/85'>{fact.value}</dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                    </div>

                    <div>
                        <Eyebrow>{founder.eyebrow}</Eyebrow>

                        <h2 className='mt-4 font-display text-[2.6rem] leading-[1.05] sm:text-6xl'>
                            {founder.title}
                        </h2>

                        <p className='mt-6 max-w-3xl text-lg leading-relaxed text-muted-foreground'>
                            {founder.intro}
                        </p>

                        <h3 className='mt-14 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground'>
                            {founder.careerTitle}
                        </h3>

                        <ol className='mt-6 border-l border-border'>
                            {founder.career.map(item => (
                                <li key={`${item.period}-${item.role}`} className='relative pb-8 pl-8 last:pb-0'>
                                    <span className='absolute -left-[5px] top-1.5 size-[9px] rounded-full border-2 border-mist bg-primary' aria-hidden='true' />

                                    <p className='font-mono text-xs text-primary'>{item.period}</p>
                                    <p className='mt-1 text-lg font-semibold leading-snug'>{item.role}</p>
                                    {item.org && <p className='text-sm font-medium text-foreground/70'>{item.org}</p>}
                                    {item.text && <p className='mt-2 max-w-2xl leading-relaxed text-muted-foreground'>{item.text}</p>}
                                </li>
                            ))}
                        </ol>

                        <h3 className='mt-14 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground'>
                            {founder.educationTitle}
                        </h3>

                        <ul className='mt-6 grid gap-3 sm:grid-cols-2'>
                            {founder.education.map(item => (
                                <li key={item.title} className='flex gap-4 rounded-2xl border border-border bg-card p-5'>
                                    <GraduationCap className='mt-0.5 size-5 shrink-0 text-primary' aria-hidden='true' />

                                    <div>
                                        <p className='font-semibold leading-snug'>{item.title}</p>
                                        <p className='mt-1 text-sm text-muted-foreground'>{item.org}</p>
                                        {item.note && <p className='mt-2 text-sm font-medium text-foreground/80'>{item.note}</p>}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            <section id='mission' className='relative overflow-hidden bg-ink py-24 text-white'>
                <div className='bg-grid-dark mask-fade-b pointer-events-none absolute inset-0 opacity-50' aria-hidden='true' />

                <div className='container-page relative grid gap-14 lg:grid-cols-2 lg:gap-20'>
                    <div>
                        <Eyebrow tone='dark'>{copy.mission.missionLabel}</Eyebrow>
                        <p className='mt-6 font-display text-3xl leading-[1.2] sm:text-[2.6rem]'>{copy.mission.mission}</p>
                    </div>

                    <div>
                        <Eyebrow tone='dark'>{copy.mission.visionLabel}</Eyebrow>
                        <p className='mt-6 font-display text-3xl leading-[1.2] text-white/80 sm:text-[2.6rem]'>{copy.mission.vision}</p>
                    </div>
                </div>
            </section>

            <section id='values' className='bg-background py-24'>
                <div className='container-page'>
                    <SectionHeader eyebrow={copy.values.eyebrow} title={copy.values.title} />

                    <ol className='mt-14 grid gap-px overflow-hidden rounded-3xl border border-border bg-border sm:grid-cols-2 lg:grid-cols-3'>
                        {copy.values.items.map((item, index) => (
                            <li key={item.title} className='bg-card p-8'>
                                <span className='font-mono text-xs text-primary'>{String(index + 1).padStart(2, '0')}</span>
                                <h3 className='mt-4 font-display text-3xl'>{item.title}</h3>
                                <p className='mt-3 leading-relaxed text-muted-foreground'>{item.text}</p>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            <section id='principles' className='bg-mist py-24'>
                <div className='container-page'>
                    <SectionHeader eyebrow={copy.principles.eyebrow} title={copy.principles.title} />

                    <ul className='mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3'>
                        {copy.principles.items.map((item, index) => {
                            const Icon = principleIcons[index] ?? UserCheck

                            return (
                                <li key={item.title} className='rounded-3xl border border-border bg-card p-7'>
                                    <span className='flex size-11 items-center justify-center rounded-xl bg-accent text-primary'>
                                        <Icon className='size-5' aria-hidden='true' />
                                    </span>

                                    <h3 className='mt-6 text-lg font-semibold'>{item.title}</h3>
                                    <p className='mt-2 leading-relaxed text-muted-foreground'>{item.text}</p>
                                </li>
                            )
                        })}
                    </ul>
                </div>
            </section>

            <section id='quality' className='bg-background py-24'>
                <div className='container-page grid gap-12 lg:grid-cols-[1fr_1.6fr] lg:gap-16'>
                    <SectionHeader eyebrow={copy.quality.eyebrow} title={copy.quality.title} text={copy.quality.text} />

                    <ul className='grid gap-x-8 gap-y-8 sm:grid-cols-2'>
                        {copy.quality.items.map((item, index) => {
                            const Icon = qualityIcons[index] ?? ShieldCheck

                            return (
                                <li key={item.title} className='flex gap-4'>
                                    <Icon className='mt-0.5 size-5 shrink-0 text-primary' aria-hidden='true' />

                                    <div>
                                        <h3 className='font-semibold'>{item.title}</h3>
                                        <p className='mt-1.5 text-[0.95rem] leading-relaxed text-muted-foreground'>{item.text}</p>
                                    </div>
                                </li>
                            )
                        })}
                    </ul>
                </div>
            </section>

            <section id='languages-and-markets' className='bg-mist py-24'>
                <div className='container-page'>
                    <SectionHeader eyebrow={copy.reach.eyebrow} title={copy.reach.title} text={copy.reach.text} />

                    <div className='mt-12 grid gap-5 lg:grid-cols-[1fr_1.3fr]'>
                        <dl className='grid gap-3'>
                            {copy.reach.facts.map((fact, index) => {
                                const Icon = reachIcons[index] ?? MapPin

                                return (
                                    <div key={fact.label} className='flex items-center gap-4 rounded-2xl border border-border bg-card p-5'>
                                        <span className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-accent text-primary'>
                                            <Icon className='size-5' aria-hidden='true' />
                                        </span>

                                        <div>
                                            <dt className='text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground'>{fact.label}</dt>
                                            <dd className='mt-0.5 font-medium'>{fact.value}</dd>
                                        </div>
                                    </div>
                                )
                            })}
                        </dl>

                        <div className='rounded-3xl bg-ink p-8 text-white'>
                            <p className='text-xs font-semibold uppercase tracking-[0.18em] text-white/55'>
                                {copy.reach.marketsLabel}
                            </p>

                            <ul className='mt-6 flex flex-wrap gap-2.5'>
                                {copy.reach.markets.map(market => (
                                    <li key={market} className='rounded-full border border-white/15 px-4 py-2 text-sm text-white/85'>
                                        {market}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            <CtaBand title={copy.ctaTitle} />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps = async () => ({ props: {} })

export default AboutPage
